import { useState, useEffect } from 'react'
import { UserPlus, Edit, Trash2, RefreshCw, Eye, UsersIcon } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { SkeletonTable } from '@/components/ui/Skeleton'
import { SearchBar } from '@/components/ui/SearchBar'
import { usePermissions } from '@/hooks/usePermissions'
import { Navigate } from 'react-router-dom'
import usersService, { User, CreateUserInput, UpdateUserInput } from '@/services/usersService'
import { UserFormModal } from '@/components/UserFormModal'
import { DeleteUserModal } from '@/components/DeleteUserModal'
import { UserDetailsModal } from '@/components/UserDetailsModal'

export default function Users() {
  const { canManageUsers, isGod } = usePermissions()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active')
  const [testFilter, setTestFilter] = useState<'all' | 'test' | 'real'>('all')

  // Guard - Redireciona se não tiver permissão
  if (!canManageUsers()) {
    return <Navigate to="/" replace />
  }

  // Carregar usuários
  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await usersService.listUsers({
        page,
        limit: 20,
        search,
        includeDeleted: activeTab === 'deleted',
        ...(testFilter !== 'all' && { isTestUser: testFilter === 'test' })
      })
      setUsers(response.users)
      setTotalPages(response.pagination.totalPages)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setPage(1)
  }, [activeTab, testFilter])

  useEffect(() => {
    loadUsers()
  }, [page, search, activeTab, testFilter])

  // Criar usuário
  const handleCreate = async (data: CreateUserInput) => {
    await usersService.createUser(data)
    setShowCreateModal(false)
    loadUsers()
  }

  // Editar usuário
  const handleEdit = async (data: UpdateUserInput) => {
    if (!selectedUser) return
    await usersService.updateUser(selectedUser.id, data)
    setShowEditModal(false)
    setSelectedUser(null)
    loadUsers()
  }

  // Deletar usuário
  const handleDelete = async () => {
    if (!selectedUser) return
    await usersService.deleteUser(selectedUser.id)
    setShowDeleteModal(false)
    setSelectedUser(null)
    loadUsers()
  }

  // Recuperar usuário
  const handleRestore = async (user: User) => {
    try {
      await usersService.restoreUser(user.id)
      loadUsers()
    } catch (error) {
      console.error('Erro ao recuperar usuário:', error)
    }
  }

  // Reload selected user data (after plan change)
  const handleUserUpdated = async () => {
    if (selectedUser) {
      try {
        const updatedUser = await usersService.getUserById(selectedUser.id)
        setSelectedUser(updatedUser)
        loadUsers() // Also refresh the table
      } catch (error) {
        console.error('Erro ao recarregar usuário:', error)
      }
    }
  }

  const getRoleBadge = (role: string) => {
    const colors = {
      god: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800',
      creator: 'bg-blue-100 text-blue-800',
      partner: 'bg-green-100 text-green-800',
      user: 'bg-gray-100 text-gray-800',
    }
    return colors[role as keyof typeof colors] || colors.user
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
    }
    return colors[status as keyof typeof colors] || colors.inactive
  }

  const getPlanBadge = (slug: string | null) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-100 text-gray-700',
      tier1: 'bg-emerald-100 text-emerald-800',
      tier2: 'bg-blue-100 text-blue-800',
      tier3: 'bg-amber-100 text-amber-800',
    }
    return colors[slug || ''] || colors.free
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Usuários</h1>
          <p className="text-sm text-gray-600 mt-1">Gerencie todos os usuários do sistema</p>
        </div>
        {activeTab === 'active' && (
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary w-full sm:w-auto">
            <UserPlus className="mr-2 h-4 w-4" />
            Novo Usuário
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
          <button
            onClick={() => setActiveTab('active')}
            className={`${activeTab === 'active'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-1 text-sm font-medium`}
          >
            Usuários Ativos
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`${activeTab === 'deleted'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-1 text-sm font-medium`}
          >
            Usuários Deletados
          </button>
        </nav>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Buscar usuários..."
            />
          </div>
          <select
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value as typeof testFilter)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Todos os usuários</option>
            <option value="real">Somente cadastros reais</option>
            <option value="test">Somente usuários de teste</option>
          </select>
        </div>

        {/* Users Table */}
        {loading ? (
          <SkeletonTable rows={5} columns={6} />
        ) : users.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title={activeTab === 'active' ? "Nenhum usuário ativo" : "Nenhum usuário deletado"}
            description={
              activeTab === 'active'
                ? "Não há usuários ativos cadastrados no sistema."
                : "Não há usuários deletados. Eles aparecerão aqui quando forem removidos."
            }
            action={
              activeTab === 'active' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-500"
                >
                  <UserPlus className="h-4 w-4" />
                  Criar Usuário
                </button>
              )
            }
          />
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="block lg:hidden space-y-4">
              {users.map((user) => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {user.name}
                        {user.is_test_user && (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                            teste
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(user.status)}`}>
                      {user.status}
                    </span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadge(user.plan_slug)}`}>
                      {user.plan_name || 'Sem plano'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Criado em {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    {activeTab === 'active' ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setShowDetailsModal(true)
                          }}
                          className="flex-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md border border-gray-200 flex items-center justify-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Ver
                        </button>
                        {(user.role !== 'god' || isGod()) && (
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setShowEditModal(true)
                            }}
                            className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200 flex items-center justify-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Editar
                          </button>
                        )}
                        {(user.role !== 'god' || isGod()) && (
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setShowDeleteModal(true)
                            }}
                            className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200 flex items-center justify-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Deletar
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(user)}
                        className="w-full px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md border border-green-200 flex items-center justify-center gap-1"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Recuperar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => { setSelectedUser(user); setShowDetailsModal(true) }}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {user.name}
                          {user.is_test_user && (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-normal">
                              teste
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadge(user.plan_slug)}`}>
                          {user.plan_name || 'Sem plano'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        {activeTab === 'active' ? (
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {(user.role !== 'god' || isGod()) && (
                              <button
                                onClick={() => {
                                  setSelectedUser(user)
                                  setShowEditModal(true)
                                }}
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {(user.role !== 'god' || isGod()) && (
                              <button
                                onClick={() => {
                                  setSelectedUser(user)
                                  setShowDeleteModal(true)
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Deletar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRestore(user)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Recuperar usuário"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Recuperar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0 sm:space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-full sm:w-auto px-4 py-2 border rounded disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-4 py-2 text-sm">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-full sm:w-auto px-4 py-2 border rounded disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <UserFormModal
          title="Criar Usuário"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
        />
      )}

      {showEditModal && selectedUser && (
        <UserFormModal
          title="Editar Usuário"
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false)
            setSelectedUser(null)
          }}
          onSubmit={handleEdit}
        />
      )}

      {showDeleteModal && selectedUser && (
        <DeleteUserModal
          userName={selectedUser.name}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedUser(null)
          }}
          onConfirm={handleDelete}
        />
      )}

      <UserDetailsModal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setSelectedUser(null) }}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />
    </div>
  )
}